import {FamilyApi} from '@/api/family-api.js';
import orderBy from 'lodash/orderBy';
import {ProductApi} from '@/api/product.js';

export const ConfiguratorTypes = Object.freeze({
  PROJECT_ONE: 'ProjectOne',
});

export const ActionTypes = Object.freeze({
  GET_FAMILY_DETAILS_DATA: 'getFamilyDetailsData',
  GET_ALL_MODEL_OPTIONS: 'getAllModelOptions',
  CONFIRM_MODEL_CHANGE: 'confirmModelChange',
});

export const MutationTypes = Object.freeze({
  SET_FAMILY_DETAILS_DATA: 'setFamilyDetailsData',
  SET_SELECTED_MODEL_ID: 'setSelectedModelId',
  SET_MODELS: 'setModels',
  SET_MODEL_OPTION_SPECS_ACTIVE: 'setAreModelOptionSpecsActive',
  SET_HAS_CUSTOMIZABLE_MODEL: 'setHasCustomizableModel',
  SET_MODEL_CHANGE_CONFIRM_CALLBACK: 'setModelChangeConfirmCallback',
  SET_MODEL_CHANGE_CANCEL_CALLBACK: 'setModelChangeCancelCallback',
  SET_IS_BUY_ZONE_LOADING: 'setIsBuyZoneLoading',
  SET_HAS_ERROR_LOADING_FAMILY: 'setHasErrorLoadingFamily',
});

const findLowestPrice = function (subfamilies, prices = {lowestPrice: null, formattedPrice: null}) {
  subfamilies.forEach((subFamily) => {
    if (subFamily?.startingAtPrice?.value && !isNaN(subFamily.startingAtPrice.value)) {
      if (prices.lowestPrice === null || subFamily.startingAtPrice.value < prices.lowestPrice) {
        prices.lowestPrice = subFamily.startingAtPrice.value;
        prices.formattedPrice = subFamily.startingAtPrice.formatted;
      }
    }
    if (Array.isArray(subFamily.subfamilies) && subFamily.subfamilies.length) {
      findLowestPrice(subFamily.subfamilies, prices);
    }
  });
};

const getModelPrice = function (model) {
  return (
    model?.prices?.consumerPrice?.advertised?.low?.value ?? model?.prices?.consumerPrice?.price?.low?.value ?? -Infinity
  );
};

// Recursively order subfamilies (and their nested subfamilies) by lowest startingAtPrice (desc)
const orderSubfamilies = function (subfamilies) {
  return orderBy(
    subfamilies.map((subfamily) => {
      // Recursively sort nested subfamilies if present
      if (Array.isArray(subfamily.subfamilies) && subfamily.subfamilies.length) {
        return {
          ...subfamily,
          subfamilies: orderSubfamilies(subfamily.subfamilies),
        };
      }
      return subfamily;
    }),
    [(subfamily) => subfamily?.startingAtPrice?.value ?? -Infinity],
    ['desc']
  );
};

const familyDetails = {
  namespaced: true,
  state: {
    familyDetailsData: {},
    buyZoneId: 'family-details-center-container',
    selectedModelId: '',
    models: [],
    areModelOptionSpecsActive: false,
    hasCustomizableModels: false,
    modelChangeConfirmCallback: () => {},
    modelChangeCancelCallback: () => {},
    isPageLoading: false,
    isBuyZoneLoading: false,
    hasErrorLoadingFamily: false,
  },
  getters: {
    selectedModel(state) {
      return state.models?.find((model) => model?.code === state.selectedModelId);
    },
    isFDP(_, getters) {
      return !!getters.selectedModel;
    },
    hasSubfamilies(state) {
      return !!state.familyDetailsData?.subfamilies?.length;
    },
    familyShortSpecsToHighlight(state) {
      return state.familyDetailsData?.specAttributes;
    },
    familyStartingPrice(state) {
      if (state.familyDetailsData?.startingAtPrice) {
        return state.familyDetailsData.startingAtPrice.formatted;
      }
      const subfamilies = state.familyDetailsData?.subfamilies;
      if (Array.isArray(subfamilies) && subfamilies.length) {
        let prices = {lowestPrice: null, formattedPrice: null};
        findLowestPrice(subfamilies, prices);
        return prices.formattedPrice || null;
      }
      return null;
    },
    isSubFamilyOnSale: (state) => (modelIds) => {
      if (modelIds?.length) {
        const models = state.models?.filter((model) => modelIds?.includes(model?.code));
        return !!models?.some((model) => model.saleFlag);
      }
    },
  },
  mutations: {
    [MutationTypes.SET_FAMILY_DETAILS_DATA](state, payload) {
      state.familyDetailsData = payload;
    },
    [MutationTypes.SET_SELECTED_MODEL_ID](state, payload) {
      state.selectedModelId = payload;
    },
    [MutationTypes.SET_MODELS](state, payload) {
      state.models = payload;
    },
    [MutationTypes.SET_MODEL_OPTION_SPECS_ACTIVE](state, payload) {
      state.areModelOptionSpecsActive = payload;
    },
    [MutationTypes.SET_HAS_CUSTOMIZABLE_MODEL](state, payload) {
      state.hasCustomizableModel = payload;
    },
    [MutationTypes.SET_MODEL_CHANGE_CONFIRM_CALLBACK](state, payload) {
      state.modelChangeConfirmCallback = payload;
    },
    [MutationTypes.SET_MODEL_CHANGE_CANCEL_CALLBACK](state, payload) {
      state.modelChangeCancelCallback = payload;
    },
    [MutationTypes.SET_IS_BUY_ZONE_LOADING](state, payload) {
      state.isBuyZoneLoading = payload;
    },
    [MutationTypes.SET_HAS_ERROR_LOADING_FAMILY](state, payload) {
      state.hasErrorLoadingFamily = payload;
    },
  },
  actions: {
    async [ActionTypes.GET_FAMILY_DETAILS_DATA]({state, commit, dispatch}, payload) {
      try {
        const response = await FamilyApi.getFamilyById(payload);
        if (response?.subfamilies?.length) {
          const orderedSubfamilies = orderSubfamilies(response.subfamilies);
          response.subfamilies = orderedSubfamilies;
        }
        commit(MutationTypes.SET_FAMILY_DETAILS_DATA, response);
        await dispatch(ActionTypes.GET_ALL_MODEL_OPTIONS);

        // If we hit the FDP with a model code in the URL we do not want to override that selection
        // If we don't have a model ID in the URL we want to set selectedModelId
        let firstModelCode = null;
        let current = state.familyDetailsData?.subfamilies?.[0];
        while (current && !firstModelCode) {
          if (Array.isArray(current.models) && current.models.length) {
            firstModelCode = current.models.reduce((highest, modelCode) => {
              const model = state.models.find((m) => m.code === modelCode);
              if (!model) return highest;
              if (!highest) return model.code;
              const highestModel = state.models.find((m) => m.code === highest);
              return getModelPrice(model) > getModelPrice(highestModel) ? model.code : highest;
            }, null);
            break;
          }
          current = Array.isArray(current.subfamilies) && current.subfamilies.length ? current.subfamilies[0] : null;
        }
        if (state.selectedModelId && state.models.some((model) => model.code === state.selectedModelId)) {
          return;
        }
        const selectedModelIdToSet = firstModelCode || state.models?.[0]?.code;

        if (selectedModelIdToSet) {
          commit(MutationTypes.SET_SELECTED_MODEL_ID, selectedModelIdToSet);
        }
      } catch (error) {
        console.error('Error retrieving family details data', error);
      }
    },

    async [ActionTypes.GET_ALL_MODEL_OPTIONS]({state, getters, commit, rootState}) {
      commit(MutationTypes.SET_IS_BUY_ZONE_LOADING, true);

      let modelIds = [];
      if (getters.hasSubfamilies) {
        const collectModels = (subfamilies) => {
          return subfamilies.flatMap((subfamily) => {
            let subfamilyModels = subfamily.models || [];
            if (Array.isArray(subfamily.subfamilies) && subfamily.subfamilies.length) {
              subfamilyModels = subfamilyModels.concat(collectModels(subfamily.subfamilies));
            }
            return subfamilyModels;
          });
        };
        modelIds = collectModels(state.familyDetailsData.subfamilies);
      } else {
        modelIds = state.familyDetailsData.models;
      }

      const models = await Promise.all(
        modelIds.map(async (modelId) => {
          const [modelData, extendedModelData] = await Promise.all([
            ProductApi.fetchFullProduct({code: modelId, isB2b: rootState.backend?.b2b}),
            ProductApi.getExtendedProductDataById(modelId),
          ]);
          return {...extendedModelData, ...modelData};
        })
      );

      const hasCustomizableModel = models.some((model) => {
        return (
          model?.customConfiguratorType === ConfiguratorTypes.PROJECT_ONE ||
          model?.variants?.some((variant) => variant?.customConfiguratorType === ConfiguratorTypes.PROJECT_ONE)
        );
      });

      commit(MutationTypes.SET_HAS_CUSTOMIZABLE_MODEL, hasCustomizableModel);
      const sortedModels = orderBy(models, 'prices.consumerPrice.price.low.value', 'desc');
      commit(MutationTypes.SET_MODELS, sortedModels);
      commit(MutationTypes.SET_IS_BUY_ZONE_LOADING, false);
    },

    [ActionTypes.CONFIRM_MODEL_CHANGE]({commit, rootState}, {confirmCallback, cancelCallback}) {
      if (rootState.productDetails.isCustomizationMode) {
        rootState.bikeCustomization.isModelChange = true;
        rootState.bikeCustomization.isCustomizeDiscardDialogOpen = true;

        // Assign the router info coming in through callback param, suspend user's choice temporarily
        commit(MutationTypes.SET_MODEL_CHANGE_CONFIRM_CALLBACK, confirmCallback);
        commit(MutationTypes.SET_MODEL_CHANGE_CANCEL_CALLBACK, cancelCallback);
      } else {
        confirmCallback();
      }
    },
  },
};

export default familyDetails;
